import { useState } from 'react';

import { ChevronsUpDown, Loader2 } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { useGetGames } from '@/api/getGames';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Text } from '@/components/ui/typography';
import { getGameImage } from '@/hooks/useGameImage';
import { useGameSwitch } from '@/hooks/useGameSandboxSwitch';

interface GameBreadcrumbDropdownProps {
  gameCode: string;
  gameName: string;
}

const GameLogo = ({ code, name }: { code: string; name: string }) => (
  <img
    className="size-5 rounded-sm object-cover"
    src={getGameImage(code)}
    alt={name}
  />
);

export const GameBreadcrumbDropdown = ({
  gameCode,
  gameName,
}: GameBreadcrumbDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { studioCode = '' } = useParams<{ studioCode: string }>();
  const { data: gamesData, isLoading } = useGetGames();
  // Switching Game stays inside the current Studio; the Studio crumb changes Studio.
  const studioGames =
    gamesData?.games.filter((game) => game.studio_code === studioCode) ?? [];

  const { switchGame } = useGameSwitch({
    onBeforeSwitch: () => {
      setIsOpen(false);
    },
  });

  const handleGameChange = (newGameCode: string) => {
    switchGame(studioCode, gameCode, newGameCode);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      {/* Same labelled whole-crumb trigger as the Sandbox crumb. */}
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 min-w-0 gap-1.5 px-2"
          title="Switch game"
          disabled={isLoading}
        >
          <Text variant="caption">Game</Text>
          <GameLogo code={gameCode} name={gameName} />
          <span className="truncate max-w-48 text-foreground">{gameName}</span>
          {isLoading ? (
            <Loader2 className="animate-spin" />
          ) : (
            <ChevronsUpDown className="opacity-50" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-xs">
        <DropdownMenuRadioGroup
          value={gameCode}
          onValueChange={handleGameChange}
          className="max-h-48 overflow-y-auto"
        >
          {studioGames.map((game) => (
            <DropdownMenuRadioItem key={game.game_code} value={game.game_code}>
              <GameLogo code={game.game_code} name={game.name} />
              <span className="truncate">{game.name}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
